import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { useGetBeneficiaryWalletQuery } from '@/api/v1';
import { WalletAddressInput } from '@/components/molecules/WalletAddressInput';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { ChainType } from '@/types/chainType';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly currency: CurrencyCodeType;
}

export const WalletDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { currency } = props;

  const [chain, setChain] = React.useState<ChainType>(ChainType.POLY);

  const customer = useCustomer();
  const {
    data: wallet = { address: '' },
    isError,
    isFetching,
  } = useGetBeneficiaryWalletQuery(
    {
      beneficiaryId: customer.beneficiaryId ?? '',
      chain: chain,
    },
    {
      skip: !customer.beneficiaryId,
    },
  );

  const tr = useTranslator();
  const onChainChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    switch (value) {
      case ChainType.POLY:
      case ChainType.SOL:
      case ChainType.BASE:
      case ChainType.TRX:
      case ChainType.MATIC:
      case ChainType.XLM:
        setChain(value);
        break;
      default:
        console.warn('invalid chain value: ', value);
    }
  }, []);

  if (isFetching) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('LOADING_PLEASE_WAIT')}&hellip;
        </Typography>
      </Box>
    );
  } else if (isError) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('ACCOUNT_INFORMATION_ERROR')}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box sx={styles.container}>
        <Typography variant="h7" color="custom.LightGray">
          {tr('WALLET_INFORMATION_WITH_CURRENCY', currency)}
        </Typography>
        <Box display="flex" flexDirection="column" gap={2} mt={3}>
          <TextField
            label={tr('CHAIN')}
            value={chain}
            select={true}
            fullWidth={true}
            onChange={onChainChange}>
            <MenuItem value={ChainType.POLY}>Polygon</MenuItem>
            <MenuItem value={ChainType.TRX}>Tron</MenuItem>
          </TextField>
          <WalletAddressInput label={tr('ADDRESS')} value={wallet.address} fullWidth={true} />
        </Box>
      </Box>
    );
  }
};

const styles: Record<string, any> = {
  container: {
    mt: 3,
    px: 4,
    py: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
};
